import { ArrowRight, Info } from "lucide-react";
import pluralize from "pluralize";

import {
  IgCoreTextColors,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { IgNumericalCounter } from "@intelligems/ig-theme/IgNumericalCounter";
import { IgSegmentedControl } from "@intelligems/ig-theme/IgSegmentedControl";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import type {
  BundleDiscount,
  BundleDiscountStructure,
} from "@intelligems/ig-types/dtos/bundle-discount";
import type { UpsellDiscountType } from "@intelligems/ig-types/dtos/upsell-product-rules";
import { UpsellDiscountTypeList } from "@intelligems/ig-types/dtos/upsell-product-rules";
import { Box, MenuItem, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { DiscountValueInput } from "@src/components/Utils/DiscountValueInput";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { DISCOUNT_TYPE_LABELS } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/options";
import {
  bundleDiscountSummary,
  bundleMinimum,
  rebaseTiers,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsell/bundleTiers";
import { useCurrency } from "@src/utils/useCurrency";

// So a newly enabled discount doesn't open with invalid empty tiers.
const DEFAULT_TIER_VALUES: Record<"percentage" | "fixed", number[]> = {
  percentage: [10, 15, 20],
  fixed: [5, 10, 15],
};

const STRUCTURE_OPTIONS: { label: string; value: BundleDiscountStructure }[] = [
  { label: "Fixed", value: "fixed" },
  { label: "Tiered", value: "tiered" },
];

export const BundleDiscountSection = ({
  discount,
  structure,
  maxProducts,
  errors,
  onChange,
  onStructureChange,
}: {
  discount: BundleDiscount | null;
  structure: BundleDiscountStructure;
  maxProducts: number;
  errors?: Record<string, string>;
  onChange: (next: BundleDiscount | null) => void;
  onStructureChange: (next: BundleDiscountStructure) => void;
}) => {
  const theme = useTheme();
  const { symbol } = useCurrency();

  const changeType = (discountType: UpsellDiscountType) => {
    if (discountType === "none") {
      return onChange(null);
    }
    onChange(
      discount
        ? { ...discount, discountType }
        : {
            discountType,
            discountTiers: rebaseTiers(
              DEFAULT_TIER_VALUES[discountType].map((value, index) => ({
                minimumProducts: index + 1,
                value,
              })),
              {
                minimum: 2,
                maxProducts,
                structure,
              }
            ),
            compareAtPriceDisplay: "regularPrice",
          }
    );
  };

  const rebase = (minimum: number, nextStructure: BundleDiscountStructure) =>
    discount &&
    onChange({
      ...discount,
      discountTiers: rebaseTiers(discount.discountTiers, {
        minimum,
        maxProducts,
        structure: nextStructure,
      }),
    });

  const summary = discount && bundleDiscountSummary(discount, symbol);

  return (
    <Stack spacing={5}>
      <Panel.Header>Discount</Panel.Header>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <IgTypography>Discount type</IgTypography>
        <IgSelect<UpsellDiscountType>
          size="small"
          sx={{ width: 200 }}
          value={discount?.discountType ?? "none"}
          onChange={(e) => changeType(e.target.value)}
        >
          {UpsellDiscountTypeList.map((type) => (
            <MenuItem key={type} value={type}>
              {DISCOUNT_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </IgSelect>
      </Stack>

      {discount && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <IgTypography>Discount structure</IgTypography>
            <IgSegmentedControl<BundleDiscountStructure>
              ariaLabel="Discount structure"
              allowDeselect={false}
              variant="subtle"
              options={STRUCTURE_OPTIONS}
              value={structure}
              onChange={(next) => {
                if (!next) {
                  return;
                }
                onStructureChange(next);
                rebase(bundleMinimum(discount), next);
              }}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={4}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <IgTypography>
                Minimum products required to receive discount
              </IgTypography>
              <IgTooltip title="Sets how many products from the bundle a customer must have in their cart to qualify for the discount.">
                <Info size={16} />
              </IgTooltip>
            </Stack>
            <IgNumericalCounter
              value={bundleMinimum(discount)}
              min={1}
              max={maxProducts}
              onChange={(minimum) =>
                minimum != null && rebase(minimum, structure)
              }
            />
          </Stack>

          <Stack spacing={3}>
            <Stack
              sx={{
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "6px",
              }}
            >
              {discount.discountTiers.map((tier, index) => (
                <Stack
                  key={tier.minimumProducts}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 4,
                    py: 3,
                    ...(index > 0 && {
                      borderTop: `1px solid ${theme.igBorders.core.default}`,
                    }),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <IgTypography color={IgCoreTextColors.secondary}>
                        {pluralize("product", tier.minimumProducts, true)}
                      </IgTypography>
                      {index === 0 && (
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: "4px",
                            backgroundColor: IgGrayPrimitives[500],
                            color: IgCoreTextColors.tertiary,
                            fontSize: "10px",
                            fontWeight: 600,
                            lineHeight: "14px",
                          }}
                        >
                          minimum
                        </Box>
                      )}
                    </Stack>
                    <ArrowRight size={16} />
                  </Stack>
                  <DiscountValueInput
                    size="small"
                    sx={{ width: 80 }}
                    // 0 is "not entered yet": show it blank; the schema blocks Apply.
                    value={tier.value || null}
                    isPercentage={discount.discountType === "percentage"}
                    error={Boolean(
                      errors?.[`discount.discountTiers.${index}.value`]
                    )}
                    onChange={(value) =>
                      onChange({
                        ...discount,
                        discountTiers: discount.discountTiers.map((t, i) =>
                          i === index ? { ...t, value } : t
                        ),
                      })
                    }
                  />
                </Stack>
              ))}
            </Stack>

            {summary && (
              <IgTypography variant="body2" color={IgCoreTextColors.secondary}>
                {summary}
              </IgTypography>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};
